import React, { ChangeEvent, DragEvent } from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";
import FileUploadDialog from "../FileUploadDialog";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import { formatDateLocal, formatName, formatDateTimeLocalNumeric, formatDateTimeRelative } from "../../Utilities";


interface ISurgeryDocumentsViewState {
    isLoading: boolean | string;
    isUploadDialogOpen: boolean;
    isDeleteOpen: boolean;

    documents: any[];

    allDocumentTypes: any[];
    uploadDocumentType: string;
    patientId: string;

    uploadDragCounter: number;
    uploadFilesOverride: FileList | undefined;
}

class SurgeryDocumentsView extends React.Component<RouteComponentProps<any>, ISurgeryDocumentsViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: ISurgeryDocumentsViewState = {
            isLoading: true,
            isUploadDialogOpen: false,
            isDeleteOpen: false,

            documents: [],

            allDocumentTypes: [],
            uploadDocumentType: "",
            patientId: this.props.match.params.patient_id,

            uploadDragCounter: 0,
            uploadFilesOverride: undefined
        };

        this.state = state;

        this.openDocument = this.openDocument.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.uploadSuccess = this.uploadSuccess.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragDrop = this.handleDragDrop.bind(this);
    }

    public componentDidMount() {
        this.loadDocuments();
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-box">{this.state.isLoading}</div>
            </div>;
        }

        if (this.state.isLoading === true) {
            return <div className="viewer-root spinner-full-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        return (
            <div className={"viewer-root full-height" + (this.state.uploadDragCounter > 0 ? " photo-drag" : "")} onDragEnter={this.handleDragEnter} onDragLeave={this.handleDragLeave} onDragOver={this.handleDragOver} onDrop={this.handleDragDrop}>
                <ViewToolbar>
                    <ToolbarMenuItem right="surgery:doc:upload">
                        <button className="icon-upload" title="Upload document..." onClick={this.toggleUploadDialog} />
                    </ToolbarMenuItem>
                    <ToolbarMenuItem right="surgery:doc:upload">
                        <Link to={{ pathname: `${this.props.location.pathname}/add`, state: { surgeryId: this.props.match.params.id } }} className="icon-plus" title="Add Document" />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <FileUploadDialog
                    isOpen={this.state.isUploadDialogOpen}
                    onRequestClose={this.toggleUploadDialog}
                    closeOnSuccess={true}
                    onUploadSuccess={this.uploadSuccess}
                    preventUpload={!this.state.uploadDocumentType}
                    accept="application/pdf,image/*,text/plain"
                    endpoint="document/upload"
                    filesOverride={this.state.uploadFilesOverride}
                    extraData={{
                        documentType: this.state.uploadDocumentType,
                        documentTypeVersion: this.state.allDocumentTypes.find(a => a.id === this.state.uploadDocumentType)?.latestVersion?.version
                    }}>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-document-type">Document type</label>
                            <select id="frm-document-type" name="uploadDocumentType" value={this.state.uploadDocumentType} onChange={this.handleInputChanged}>
                                <option value="" />
                                <optgroup label="Imported Documents">
                                    {this.state.allDocumentTypes.filter(a => a.importOnly).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </optgroup>
                                <optgroup label="Digital Forms">
                                    {this.state.allDocumentTypes.filter(a => !a.importOnly).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </optgroup>
                            </select>
                        </div>
                    </section>
                    <div>
                        <small>
                            <br />
                            If you want to import multiple documents that have barcodes, click <Link to={{ pathname: "/patient/document-import", state: { defaultUploadOpen: true } }}>here</Link>
                        </small>
                    </div>
                </FileUploadDialog>

                <p>
                    Listed below are documents in the patient's chart specific to this surgery
                </p>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Date Added</th>
                            <th>Description</th>
                            <th className="col-smallest">Added by</th>
                            <th className="col-smallest">Last Modified</th>
                            <th className="col-smallest"><i className="icon-question" title="Document status" /></th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Chart documents</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.documents.length === 0
                            ? <tr>
                                <td colSpan={10}>No forms could be found for this surgery</td>
                            </tr>
                            : this.state.documents.map(a => (
                                <tr key={a.id} className="clickable" onClick={() => this.openDocument(a.id)}>
                                    <td>{formatDateLocal(a.creationTimestamp)}</td>
                                    <td className="wrap"><span title={`v${a.documentVersion}`}>{a.name}</span></td>
                                    <td>{formatName(a.creator?.name)}</td>
                                    <td><span title={formatDateTimeRelative(a.modificationTimestamp)}>{formatDateTimeLocalNumeric(a.modificationTimestamp)}</span></td>
                                    <td>
                                        {a.lockedBy ? <i className="icon icon-lock" title={`Locked by ${formatName(a.lockedBy.name)}`} /> : null}
                                        {a.isFinalized ? <i className="icon icon-check" title="Finalized" /> : null}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const result = await V2.get<any>(`document/types?limit=100&show_all=true`);

            this.setState({
                isLoading: false,
                allDocumentTypes: result.documentTypes
            });

            await this.loadPatientId();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load data"
            });
        }
    }

    private async loadDocuments() {
        try {
            this.setState({
                isLoading: true
            });

            const documents = await V2.get<any>(`surgery/${this.props.match.params.id}/documents`);

            this.setState({
                isLoading: false,

                documents: documents.forms
            });

            await this.loadData();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load documents"
            });
        }
    }

    private openDocument(id: string) {
        this.props.history.push(`/surgery/${this.props.match.params.id}/documents/${id}`);
    }

    private toggleUploadDialog() {
        const { isUploadDialogOpen } = this.state;
        this.setState({
            isUploadDialogOpen: !isUploadDialogOpen,
            uploadFilesOverride: undefined
        });
    }

    private async uploadSuccess(ids: string[], responses?: any[]) {
        // Document is uploaded, attach to patient and/or surgery
        const documentId = responses![0].documentId;

        await V2.put<void>(`patients/${this.state.patientId}/documents/link/${documentId}`);
        if (this.props.match.params.id) {
            // Link document to surgery too
            await V2.put<void>(`surgery/${this.props.match.params.id}/documents/link/${documentId}`);
        }

        window.setTimeout(() => this.loadDocuments(), 100);
    }

    private handleInputChanged(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private async loadPatientId() {
        return new Promise<void>((resolve, reject) => {
            if (this.props.match.params.patient_id) {
                this.setState({
                    patientId: this.props.match.params.patient_id
                }, () => resolve());
                return;
            }

            if (this.props.match.params.id) {
                // Look up patient ID from surgery
                V2.get<any>(`surgery/${this.props.match.params.id}/patient`)
                    .then((info) => {
                        console.log(info);
                        this.setState({
                            patientId: info.patientId
                        }, () => resolve());
                    })
                return;
            }
        });
    }

    private handleDragEnter(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";

        const uploadDragCounter = this.state.uploadDragCounter;

        this.setState({
            uploadDragCounter: uploadDragCounter + 1
        });
    }

    private handleDragLeave(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";

        const uploadDragCounter = this.state.uploadDragCounter;

        this.setState({
            uploadDragCounter: uploadDragCounter - 1
        });
    }

    private handleDragOver(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";
    }

    private handleDragDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.persist();

        this.setState({
            uploadDragCounter: 0,
            uploadFilesOverride: e.dataTransfer.files.length > 0
                ? e.dataTransfer.files
                : undefined,
            isUploadDialogOpen: true
        });
    }
}

export default withTitle("Surgery Documents", SurgeryDocumentsView);
